import React, { useState, useMemo, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Clock, Users, BookOpen, MapPin, Check, X, Calendar, Info, ChevronLeft, ChevronRight, Search, CalendarPlus, CalendarRange, AlertCircle } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { api } from '../services/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Button } from './ui/button';

// Les deux boutons d'un pied de modal ont la meme taille ET la meme largeur.
// DESIGN-SYSTEM.md, section Boutons.
const BOUTON_PIED = 'h-9 min-w-[168px] justify-center gap-2';

const WEEK_DAYS = [
  { id: 1, label: 'Lundi' },
  { id: 2, label: 'Mardi' },
  { id: 3, label: 'Mercredi' },
  { id: 4, label: 'Jeudi' },
  { id: 5, label: 'Vendredi' }
];

// `SLOT_TYPES` vivait ici : declaree, jamais lue, et porteuse de trois
// emojis doublement encodes que la reparation d'encodage ne pouvait pas
// rendre. Les deux cartes de choix sont ecrites en clair dans le rendu.

const SimpleModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  classes, 
  subjects, 
  activeYear,
  onOpenEmploiDuTemps
}) => {
  // États
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState(null);
  const [enregistrement, setEnregistrement] = useState(false);
  const inputRef = useRef(null);
  
  // États pour les enseignants et affectations


 const [formData, setFormData] = useState({
  type: 'unique',
  date: new Date(),
  trimester: null,
  days: [],
  timeSlot: { start: '08:00', end: '09:00' },
  enseignant_id: '',
  affectation_id: '' // Nouveau champ
});

// Nouveaux états pour les affectations
// États pour les enseignants et affectations

const [teachers, setTeachers] = useState([]);  
const [teacherSearchTerm, setTeacherSearchTerm] = useState('');
const [teacherAffectations, setTeacherAffectations] = useState([]);
const [loadingAffectations, setLoadingAffectations] = useState(false);
const [loadingTeachers, setLoadingTeachers] = useState(false);


useEffect(() => {
  const loadTeachersData = async () => {
    if (!activeYear) return;
    
    setLoadingTeachers(true);
    try {
      // Utiliser votre API existante
      const response = await api.employes.getEnseignantsAvecAffectationsDetaillees(activeYear.id);
      
      if (response.success) {
        // Filtrer pour garder seulement les enseignants avec affectations
        const teachersWithAffectations = response.data.filter(teacher => 
          teacher.affectations && teacher.affectations.length > 0
        );
        setTeachers(teachersWithAffectations);
        console.log('Teachers chargés dans modal:', teachersWithAffectations); // Debug
      } else {
        console.error('Erreur lors du chargement des enseignants');
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoadingTeachers(false);
    }
  };

  if (activeYear) {
    loadTeachersData();
  }
}, [activeYear]);


  // Étapes du processus
  const steps = [
    { title: 'Type de créneau', description: 'Choisissez le type de créneau à créer' },
    { title: 'Planification', description: 'Définissez la période et les jours' },
    { title: 'Horaires', description: 'Configurez les horaires' },
    { title: 'Attribution', description: 'Détails du cours' }
  ];

  // Calcul des trimestres
  const trimesters = useMemo(() => {
    if (!activeYear?.debut || !activeYear?.fin) {
      return [
        {
          id: 1,
          label: '1er Trimestre',
          debut: new Date(new Date().getFullYear(), 9, 1),
          fin: new Date(new Date().getFullYear(), 11, 31)
        },
        {
          id: 2,
          label: '2ème Trimestre',
          debut: new Date(new Date().getFullYear(), 0, 1),
          fin: new Date(new Date().getFullYear(), 2, 31)
        },
        {
          id: 3,
          label: '3ème Trimestre',
          debut: new Date(new Date().getFullYear(), 3, 1),
          fin: new Date(new Date().getFullYear(), 5, 30)
        }
      ];
    }
    
    try {
      const debut = new Date(activeYear.debut);
      const fin = new Date(activeYear.fin);
      
      return [
        {
          id: 1,
          label: '1er Trimestre',
          debut: new Date(debut.getFullYear(), 9, 1),
          fin: new Date(debut.getFullYear(), 11, 31)
        },
        {
          id: 2,
          label: '2ème Trimestre',
          debut: new Date(fin.getFullYear(), 0, 1),
          fin: new Date(fin.getFullYear(), 2, 31)
        },
        {
          id: 3,
          label: '3ème Trimestre',
          debut: new Date(fin.getFullYear(), 3, 1),
          fin: new Date(fin.getFullYear(), 5, 30)
        }
      ];
    } catch (error) {
      console.error("Erreur lors du calcul des trimestres:", error);
      return [
        {
          id: 1,
          label: '1er Trimestre',
          debut: new Date(new Date().getFullYear(), 9, 1),
          fin: new Date(new Date().getFullYear(), 11, 31)
        },
        {
          id: 2,
          label: '2ème Trimestre',
          debut: new Date(new Date().getFullYear(), 0, 1),
          fin: new Date(new Date().getFullYear(), 2, 31)
        },
        {
          id: 3,
          label: '3ème Trimestre',
          debut: new Date(new Date().getFullYear(), 3, 1),
          fin: new Date(new Date().getFullYear(), 5, 30)
        }
      ];
    }
  }, [activeYear?.debut, activeYear?.fin]);

  // À chaque ouverture, on repart du début. `currentStep` n'était jamais remis
  // à zéro : rouvrir le modal, c'était retomber sur la dernière étape avec la
  // saisie du créneau précédent.
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setError(null);
      setEnregistrement(false);
    }
  }, [isOpen]);

  // ⚠️ On garde ce garde-fou MALGRE Dialog. Radix ne rend pas son contenu
  // quand la fenetre est fermee, mais JSX evalue ses enfants AVANT de les lui
  // passer : `renderStepContent()` tournerait donc a chaque rendu de la page,
  // fenetre fermee comprise. Dialog tient le focus et la touche Echap ; ceci
  // evite de construire pour rien ce que personne ne regarde.
  if (!isOpen) return null;

  // Fonctions de gestion des événements
  const showError = (message) => {
    setError(message);
    setTimeout(() => setError(null), 3000);
  };

  const handleTypeSelection = (typeId) => {
    setFormData(prev => ({
      ...prev,
      type: typeId
    }));
  };

  const handleDaySelection = (dayId) => {
    setFormData(prev => ({
      ...prev,
      days: prev.days.includes(dayId)
        ? prev.days.filter(d => d !== dayId)
        : [...prev.days, dayId]
    }));
  };

  const handleTrimesterSelection = (trimester) => {
    setFormData(prev => ({
      ...prev,
      trimester
    }));
  };


// Récupérer les affectations d'un enseignant
const fetchTeacherAffectations = async (enseignantId) => {
  if (!enseignantId || !activeYear) return;
  
  setLoadingAffectations(true);
  try {
    // Trouver l'enseignant dans les données déjà chargées
    const selectedTeacher = teachers.find(t => t.id.toString() === enseignantId.toString());
    
    if (selectedTeacher && selectedTeacher.affectations) {
      // Transformer les affectations pour le format attendu
      const formattedAffectations = selectedTeacher.affectations.map(affectation => ({
        affectation_id: affectation.id,
        classe_id: affectation.classe_id,
        matiere_id: affectation.matiere_id,
        classe_nom: affectation.classe_nom,
        matiere_nom: affectation.matiere_nom || 'Enseignant principal',
        type: affectation.type
      }));
      
      setTeacherAffectations(formattedAffectations);
    } else {
      setTeacherAffectations([]);
      showError('Aucune affectation trouvée pour cet enseignant');
    }
  } catch (error) {
    console.error('Erreur:', error);
    showError('Erreur lors du chargement des affectations');
  } finally {
    setLoadingAffectations(false);
  }
};



  // Gestion de la sélection multiple de classes
  const handleClassToggle = (classId) => {
    setFormData(prev => {
      const isSelected = prev.selectedClasses.includes(classId);
      return {
        ...prev,
        selectedClasses: isSelected
          ? prev.selectedClasses.filter(id => id !== classId)
          : [...prev.selectedClasses, classId]
      };
    });
  };

  // Validation du formulaire à chaque étape
  const validateCurrentStep = () => {
    switch (currentStep) {
      case 0: // Type de créneau
        if (!formData.type) {
          showError("Veuillez sélectionner un type de créneau");
          return false;
        }
        return true;

      case 1: // Planification
        if (formData.type === 'unique' && !formData.date) {
          showError("Veuillez sélectionner une date");
          return false;
        }
        if (formData.type === 'trimestre' && !formData.trimester) {
          showError("Veuillez sélectionner un trimestre");
          return false;
        }
        if ((formData.type === 'trimestre' || formData.type === 'annuel') && formData.days.length === 0) {
          showError("Veuillez sélectionner au moins un jour de la semaine");
          return false;
        }
        return true;

      case 2: // Horaires
        if (!formData.timeSlot.start || !formData.timeSlot.end) {
          showError("Veuillez définir les horaires de début et de fin");
          return false;
        }
        
        const [startHour, startMinute] = formData.timeSlot.start.split(':').map(Number);
        const [endHour, endMinute] = formData.timeSlot.end.split(':').map(Number);
        const startTime = startHour * 60 + startMinute;
        const endTime = endHour * 60 + endMinute;

        if (endTime <= startTime) {
          showError("L'heure de fin doit être après l'heure de début");
          return false;
        }

        if (endTime - startTime > 240) { // 4 heures max
          showError("La durée maximum d'un créneau est de 4 heures");
          return false;
        }
        
        return true;

     case 3: // Attribution
        if (!formData.enseignant_id) {
          showError("Veuillez sélectionner un enseignant");
          return false;
        }
        if (!formData.affectation_id) {
          showError("Veuillez sélectionner une affectation");
          return false;
        }
        return true;

      default:
        return false;
    }
  };




  // Préparation des données pour soumission
 const prepareDataForSubmission = () => {
  // Récupérer l'affectation sélectionnée pour obtenir classe_id et matiere_id
  const selectedAffectation = teacherAffectations.find(a => a.affectation_id.toString() === formData.affectation_id.toString());
  
  if (!selectedAffectation) {
    console.error("Aucune affectation sélectionnée");
    return null;
  }

  console.log('Affectation sélectionnée:', selectedAffectation);

 
  // Base data mise à jour avec les vraies valeurs
const baseData = {
  enseignant_id: Number(formData.enseignant_id),
  classe_id: Number(selectedAffectation.classe_id),
  matiere_id: selectedAffectation.matiere_id ? Number(selectedAffectation.matiere_id) : null,
  affectation_id: Number(formData.affectation_id),
  salle: '',
  annee_scolaire_id: activeYear ? Number(activeYear.id) : null
};

  switch (formData.type) {
    case 'unique':
      if (!formData.date) {
        console.error("Pas de date sélectionnée pour le créneau unique");
        return null;
      }
      
      return {
        type_recurrence: 'unique',
        date: format(formData.date, 'yyyy-MM-dd'),
        date_debut: format(formData.date, 'yyyy-MM-dd'),
        date_fin: format(formData.date, 'yyyy-MM-dd'),
        heure_debut: formData.timeSlot.start,
        heure_fin: formData.timeSlot.end,
        enseignant_id: Number(formData.enseignant_id),
        affectation_id: Number(formData.affectation_id),
        classe_id: Number(selectedAffectation.classe_id),
        matiere_id: selectedAffectation.matiere_id ? Number(selectedAffectation.matiere_id) : null,
        annee_scolaire_id: activeYear ? Number(activeYear.id) : null,
        jour_semaine: formData.date.getDay() || 7,
        salle: '',
        // Un seul slot pour le créneau unique
        slots: [{
          enseignant_id: Number(formData.enseignant_id),
          affectation_id: Number(formData.affectation_id),
          classe_id: Number(selectedAffectation.classe_id),
          matiere_id: selectedAffectation.matiere_id ? Number(selectedAffectation.matiere_id) : null,
          jour_semaine: formData.date.getDay() || 7,
          heure_debut: formData.timeSlot.start,
          heure_fin: formData.timeSlot.end,
          annee_scolaire_id: Number(activeYear.id),
          salle: ''
        }]
      };

    case 'trimestre':
      if (!formData.trimester) return null;
      
      return {
        type_recurrence: 'trimestre',
        date_debut: format(formData.trimester.debut, 'yyyy-MM-dd'),
        date_fin: format(formData.trimester.fin, 'yyyy-MM-dd'),
        jours_semaine: formData.days,
        heure_debut: formData.timeSlot.start,
        heure_fin: formData.timeSlot.end,
        enseignant_id: Number(formData.enseignant_id),
        affectation_id: Number(formData.affectation_id),
        classe_id: Number(selectedAffectation.classe_id),
        matiere_id: selectedAffectation.matiere_id ? Number(selectedAffectation.matiere_id) : null,
        salle: '',
        annee_scolaire_id: Number(activeYear.id),
        // Créer un créneau pour chaque jour sélectionné
        slots: formData.days.map(jour_semaine => ({
          ...baseData,
          jour_semaine: Number(jour_semaine),
          heure_debut: formData.timeSlot.start,
          heure_fin: formData.timeSlot.end
        }))
      };

    case 'annuel':
      if (!activeYear) return null;
      
      return {
        type_recurrence: 'annuel',
        date_debut: format(new Date(activeYear.debut), 'yyyy-MM-dd'),
        date_fin: format(new Date(activeYear.fin), 'yyyy-MM-dd'),
        jours_semaine: formData.days,
        heure_debut: formData.timeSlot.start,
        heure_fin: formData.timeSlot.end,
        enseignant_id: Number(formData.enseignant_id),
        affectation_id: Number(formData.affectation_id),
        classe_id: Number(selectedAffectation.classe_id),
        matiere_id: selectedAffectation.matiere_id ? Number(selectedAffectation.matiere_id) : null,
        salle: '',
        annee_scolaire_id: Number(activeYear.id),
        // Créer un créneau pour chaque jour sélectionné
        slots: formData.days.map(jour_semaine => ({
          ...baseData,
          jour_semaine: Number(jour_semaine),
          heure_debut: formData.timeSlot.start,
          heure_fin: formData.timeSlot.end
        }))
      };

    default:
      return null;
  }
};

  // Soumission du formulaire
  const handleSubmit = async () => {
    if (enregistrement) return;
    setEnregistrement(true);
    try {
      if (!validateCurrentStep()) return;

      const data = prepareDataForSubmission();
      if (!data) {
        showError("Erreur de validation des données");
        return;
      }

      // `onClose()` s'exécutait juste après `onSubmit`, SANS CONDITION. Le
      // parent, lui, attrape ses erreurs sans les relancer et ne faisait rien
      // quand le serveur répondait `success: false`. Un créneau qui n'avait
      // pas été créé fermait donc le modal exactement comme un créneau créé.
      //
      // Le parent renvoie maintenant l'issue, et on ne ferme que sur un vrai
      // succès. En cas d'échec le modal reste ouvert avec la saisie : elle a
      // coûté quatre étapes.
      const issue = await onSubmit(data);

      if (issue && issue.success === false) {
        showError(issue.message || "Le créneau n'a pas pu être créé");
        return;
      }

      onClose();
    } catch (error) {
      showError(error.message || "Une erreur est survenue");
    } finally {
      setEnregistrement(false);
    }
  };

  // Navigation entre les étapes
  const goToNextStep = () => {
    if (validateCurrentStep()) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const goToPrevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  // Contenu de chaque étape
  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Type de créneau
  return (
    <div className="space-y-3">
      <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
        <Calendar className="w-4 h-4 text-[#319190]" />
        Type de créneau
      </label>
      
      <div className="grid grid-cols-2 gap-4">
        {/* Créneau unique */}
        <div
          onClick={() => handleTypeSelection('unique')}
          className={`
            p-4 rounded-xl border-2 transition-all cursor-pointer
            hover:shadow-md text-center
            ${formData.type === 'unique' 
              ? 'border-[#319190] bg-gradient-to-r from-[#319190]/10 to-[#63b5b4]/10 shadow-md' 
              : 'border-gray-200 hover:border-[#319190]/30'
            }
          `}
        >
          <CalendarPlus className="mx-auto mb-1 text-[#319190]" size={22} />
          <span className="block font-medium text-sm">Créneau unique</span>
          <p className="text-xs text-gray-500 mt-2">
            Créer un seul créneau pour une date spécifique
          </p>
        </div>

        {/* Emploi du temps annuel - NOUVEAU */}
        <div
          onClick={() => {
            if (onOpenEmploiDuTemps) {
              onOpenEmploiDuTemps();
            }
          }}
          className="p-4 rounded-xl border-2 border-[#319190]/30 hover:border-[#319190] bg-white transition-all cursor-pointer hover:shadow-md text-center hover:bg-gradient-to-r hover:from-[#319190]/5 hover:to-[#63b5b4]/5"
        >
          <CalendarRange className="mx-auto mb-1 text-[#319190]" size={22} />
          <span className="block font-medium text-sm text-[#319190]">
            Emploi du temps annuel
          </span>
          <p className="text-xs text-gray-500 mt-2">
            Planifier l'emploi du temps complet d'une classe
          </p>
        </div>
      </div>
    </div>
  );

      case 1: // Planification
        return (
          <div className="space-y-6">
            {/* Sélection de date pour type unique */}
            {formData.type === 'unique' && (
              <div className="rounded-xl bg-gradient-to-r from-[#319190]/10 to-[#63b5b4]/10 p-5">
                <style jsx global>{`
                  .full-width-calendar .rdp {
                    --rdp-cell-size: 46px;
                    --rdp-accent-color: #319190;
                    --rdp-background-color: #e6e6e6;
                    margin: 0;
                    width: 100%;
                  }
                  .full-width-calendar .rdp-month {
                    width: 100%;
                  }
                  .full-width-calendar .rdp-table {
                    width: 100%;
                    max-width: none;
                  }
                  .full-width-calendar .rdp-day {
                    width: calc(100% / 7);
                    height: 46px;
                    margin: 0 auto;
                  }
                  .full-width-calendar .rdp-day_selected {
                    background-color: #319190;
                  }
                  .full-width-calendar .rdp-day_today {
                    color: #c66d59;
                    font-weight: bold;
                  }
                  .full-width-calendar .rdp-button:hover:not([disabled]):not(.rdp-day_selected) {
                    background-color: rgba(49, 145, 144, 0.2);
                  }
                `}</style>
                <div className="full-width-calendar bg-white rounded-lg shadow-sm p-4">
                  <DayPicker
                    mode="single"
                    selected={formData.date}
                    onSelect={(date) => setFormData({...formData, date: date || new Date()})}
                    locale={fr}
                    className="!bg-white"
                  />
                </div>
              </div>
            )}

            {/* Sélection du trimestre */}
            {formData.type === 'trimestre' && (
              <div className="space-y-4">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-[#319190]" />
                  Sélection du trimestre
                </label>
                <div className="grid grid-cols-3 gap-4">
                  {trimesters.map((trim) => (
                    <div
                      key={trim.id}
                      onClick={() => handleTrimesterSelection(trim)}
                      className={`
                        p-4 rounded-xl border-2 transition-all text-center cursor-pointer
                        hover:shadow-md
                        ${formData.trimester?.id === trim.id 
                          ? 'border-[#319190] bg-gradient-to-r from-[#319190]/10 to-[#63b5b4]/10 shadow-md' 
                          : 'border-gray-200 hover:border-[#319190]/30'
                        }
                      `}
                    >
                      <span className="block font-medium">{trim.label}</span>
                      <span className="text-sm text-gray-500 mt-1">
                        {format(trim.debut, 'dd/MM')} - {format(trim.fin, 'dd/MM')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sélection de l'année scolaire pour type annuel */}
            {formData.type === 'annuel' && (
              <div className="p-6 rounded-xl bg-gradient-to-r from-[#319190]/10 to-[#63b5b4]/10 text-center shadow-inner">
                <h3 className="font-medium text-lg">
                  {activeYear?.label ? `Année scolaire ${activeYear.label}` : 'Année scolaire'}
                </h3>
                {activeYear?.debut && activeYear?.fin && (
                  <p className="text-gray-600 mt-2">
                    Du {format(new Date(activeYear.debut), 'dd/MM/yyyy')} 
                    au {format(new Date(activeYear.fin), 'dd/MM/yyyy')}
                  </p>
                )}
              </div>
            )}

            {/* Sélection des jours pour types récurrents */}
            {(formData.type === 'trimestre' || formData.type === 'annuel') && (
              <div className="space-y-3 mt-6">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-[#319190]" />
                  Jours de récurrence
                </label>
                <div className="grid grid-cols-5 gap-3">
                  {WEEK_DAYS.map((day) => (
                    <div
                      key={day.id}
                      onClick={() => handleDaySelection(day.id)}
                      className={`
                        py-3 rounded-lg transition-all cursor-pointer
                        hover:shadow-md text-center
                        ${formData.days.includes(day.id)
                          ? 'bg-[#319190] text-white shadow-md'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }
                      `}
                    >
                      {day.label}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 2: // Horaires
        return (
          <div>
            <h3 className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-4">
              <Clock className="w-4 h-4 text-[#319190]" />
              Horaires
            </h3>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm text-gray-500 flex items-center gap-1.5">
                  Heure de début
                </label>
                <input
                  type="time"
                  value={formData.timeSlot.start}
                  onChange={(e) => setFormData({...formData, timeSlot: {...formData.timeSlot, start: e.target.value}})}
                  min="07:00"
                  max="19:00"
                  className="w-full border-2 border-gray-200 rounded-lg p-3 focus:border-[#319190] focus:ring-0 transition-colors"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-gray-500 flex items-center gap-1.5">
                  Heure de fin
                </label>
                <input
                  type="time"
                  value={formData.timeSlot.end}
                  onChange={(e) => setFormData({...formData, timeSlot: {...formData.timeSlot, end: e.target.value}})}
                  min="07:00"
                  max="19:00"
                  className="w-full border-2 border-gray-200 rounded-lg p-3 focus:border-[#319190] focus:ring-0 transition-colors"
                />
              </div>
            </div>
            
            <div className="mt-6 p-3.5 rounded-lg bg-blue-50 border border-blue-100 flex items-start gap-2 text-sm text-blue-700">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>La durée maximale d'un créneau est de 4 heures. Assurez-vous que l'heure de fin soit postèrieure à l'heure de début.</p>
            </div>
          </div>
        );

     case 3: // Attribution
  return (
    <div className="space-y-6">

     
      {/* Sélection de l'enseignant avec recherche */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
              <Users className="w-4 h-4 text-[#319190]" />
              Enseignant
            </label>
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />

              <input
                ref={inputRef}
                type="text"
                placeholder="Rechercher un enseignant..."
                value={(() => {
                  const selectedTeacher = teachers.find(t => t.id.toString() === formData.enseignant_id);
                  return selectedTeacher ? `${selectedTeacher.nom} ${selectedTeacher.prenom}` : teacherSearchTerm;
                })()}
                onChange={(e) => {
                  setTeacherSearchTerm(e.target.value);
                  if (formData.enseignant_id) {
                    setFormData({...formData, enseignant_id: '', affectation_id: ''});
                    setTeacherAffectations([]);
                  }
                }}
                className="w-full pl-10 pr-10 py-3 border-2 border-gray-200 rounded-lg focus:border-[#319190] focus:ring-0 transition-colors"
              />

              {formData.enseignant_id && (
                <button
                  onClick={() => {
                    setFormData({...formData, enseignant_id: '', affectation_id: ''});
                    setTeacherSearchTerm('');
                    setTeacherAffectations([]);
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}

              {/* La liste des suggestions reste DANS la fenetre.

                  Elle passait par un portail sur <body>, donc hors du contenu
                  du dialogue : hors du piege de focus, et marquee `aria-hidden`
                  par Radix. Surtout, sa position etait lue par
                  getBoundingClientRect() PENDANT le rendu, puis figee dans un
                  style `fixed` — une mesure de mise en page faite au mauvais
                  moment, qui ne suit ni le defilement du corps ni un
                  redimensionnement. Le `+ window.scrollY` y ajoutait un
                  decalage qui n'a pas de sens pour un element `fixed`, et que
                  seul le verrou de defilement d'un dialogue ouvert masquait.

                  Posee sous le champ, en absolu dans son conteneur, elle suit
                  tout toute seule. */}
              {teacherSearchTerm && !formData.enseignant_id && (
                  <div
                    className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
                  >
                    {(() => {
                      const filteredTeachers = teachers.filter(teacher => 
                        `${teacher.nom} ${teacher.prenom}`.toLowerCase().includes(teacherSearchTerm.toLowerCase())
                      );

                      return filteredTeachers.length > 0 ? (
                        filteredTeachers.map((teacher) => (
                          <div
                            key={teacher.id}
                            onClick={() => {
                              setFormData({...formData, enseignant_id: teacher.id.toString(), affectation_id: ''});
                              setTeacherSearchTerm('');
                              fetchTeacherAffectations(teacher.id);
                            }}
                            className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">
                              {teacher.nom} {teacher.prenom}
                            </div>
                            <div className="text-xs text-gray-400">
                              Affectations: {teacher.affectations?.length || 0}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-3 text-center text-gray-500">
                          <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">Aucun enseignant trouvé pour "{teacherSearchTerm}"</p>
                        </div>
                      );
                    })()}
                  </div>
              )}
            </div>

          </div>

      {/* Affectations de l'enseignant */}
      {formData.enseignant_id && (
        <div className="space-y-3">
          <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
            <BookOpen className="w-4 h-4 text-[#319190]" />
            Affectation (Classe + Matière)
          </label>
          
          {loadingAffectations ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#319190] mx-auto"></div>
              <p className="text-sm text-gray-600 mt-2">Chargement des affectations...</p>
            </div>
          ) : teacherAffectations.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 max-h-[200px] overflow-y-auto p-1">
              {teacherAffectations.map(affectation => (
                <div
                  key={affectation.affectation_id}
                  onClick={() => setFormData({...formData, affectation_id: affectation.affectation_id})}
                  className={`
                    p-4 rounded-lg border-2 transition-all cursor-pointer
                    hover:shadow-md flex items-center justify-between
                    ${formData.affectation_id === affectation.affectation_id
                      ? 'border-[#319190] bg-gradient-to-r from-[#319190]/10 to-[#63b5b4]/10 shadow-md'
                      : 'border-gray-200 hover:border-[#319190]/30'
                    }
                  `}
                >
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">
                      {affectation.classe_nom}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {affectation.matiere_nom}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Type: {affectation.type === 'principal' ? 'Enseignant principal' : 'Matière spécifique'}
                    </div>
                  </div>
                  {formData.affectation_id === affectation.affectation_id && (
                    <Check className="w-5 h-5 text-[#319190]" />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <BookOpen className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Aucune affectation trouvée pour cet enseignant</p>
            </div>
          )}
        </div>
      )}

      {/* Résumé de l'affectation sélectionnée */}
      {formData.affectation_id && (() => {
        const selectedAffectation = teacherAffectations.find(a => a.affectation_id === formData.affectation_id);
        return selectedAffectation ? (
          <div className="mt-4 p-3 rounded-lg bg-blue-50 border border-blue-200">
            <div className="text-sm text-blue-700">
              <strong>Sélection:</strong> {selectedAffectation.classe_nom} - {selectedAffectation.matiere_nom}
            </div>
            <div className="text-xs text-blue-600 mt-1">
              Affectation ID: {selectedAffectation.affectation_id}
            </div>
          </div>
        ) : null;
      })()}
    </div>
  );

      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(ouvert) => { if (!ouvert) onClose(); }}>
      <DialogContent
        className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="space-y-0 border-b px-5 py-4 text-left">
          <DialogTitle className="text-lg font-semibold text-gray-900">
            Création de créneau
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            Un créneau unique, une récurrence, ou l'emploi du temps annuel d'une classe.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {/* Étapes - Corrigé pour éviter la ligne blanche */}
          <div className="mb-8 relative flex justify-between">
            {steps.map((step, index) => (
              <div key={index} className="flex flex-col items-center z-10 relative">
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium
                  transition-all ${index <= currentStep 
                    ? 'bg-[#319190] text-white' 
                    : 'bg-gray-100 text-gray-400'
                  }
                `}>
                  {index < currentStep ? <Check className="w-5 h-5" /> : index + 1}
                </div>
                <div className="text-xs mt-2 text-center max-w-[85px]">
                  <div className={`font-medium ${index <= currentStep ? 'text-[#319190]' : 'text-gray-400'}`}>
                    {step.title}
                  </div>
                </div>
              </div>
            ))}
            
            {/* Ligne de progression - Corrigée */}
            <div className="absolute top-5 left-0 right-0 h-[2px] bg-gray-200 -z-0"></div>
            <div 
              className="absolute top-5 left-0 h-[2px] bg-[#319190] -z-0 transition-all duration-300" 
              style={{ 
                width: `${(currentStep / (steps.length - 1)) * 100}%`,
              }}
            ></div>
          </div>

          {/* Contenu de l'étape */}
          <div className="mb-8">
            {renderStepContent()}
          </div>

          {/* Message d'erreur */}
          {error && (
            <div className="mb-6 p-3.5 rounded-lg bg-red-50 border border-red-100 flex items-center gap-2 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

        </div>

        <DialogFooter className="border-t bg-gray-50 px-5 py-4 sm:justify-between">
          <Button
            variant="outline"
            className={BOUTON_PIED}
            onClick={currentStep > 0 ? goToPrevStep : onClose}
          >
            {currentStep > 0 ? <ChevronLeft className="h-4 w-4" /> : null}
            {currentStep > 0 ? 'Retour' : 'Annuler'}
          </Button>

          {/* Plus de `scale` au survol : un bouton qui grandit deplace ce
              qui l'entoure, et c'est le bouton de validation. */}
          <Button
            onClick={currentStep < steps.length - 1 ? goToNextStep : handleSubmit}
            disabled={enregistrement}
            className={`${BOUTON_PIED} border border-transparent bg-[#246990] text-white hover:bg-[#1f5a7a]`}
          >
            {currentStep < steps.length - 1 ? (
              <>
                Continuer <ChevronRight className="h-4 w-4" />
              </>
            ) : enregistrement ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                Création...
              </>
            ) : (
              <>
                <Check className="h-4 w-4" /> Créer le créneau
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SimpleModal;